// src/components/AuthPage/index.js — Full-page Auth (Login · Signup · Forgot Password)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Phone, Mail, Lock, User, ArrowLeft, Home, Eye, EyeOff, Loader } from 'lucide-react';
import axios from 'axios';
import { getTenantId } from '../../config/tenantConfig';
import { setAuthSession as setAuthCookies } from '../../utils/authStorage';
import './index.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const X_API_KEY = process.env.REACT_APP_X_API_KEY;

const getApiHeaders = () => ({
  'Content-Type': 'application/json',
  'X-Api-Key': X_API_KEY,
  'X-Tenant': getTenantId(),
  'Accept': 'application/json'
});

const OtpBoxInput = ({ length = 4, value = '', onChange }) => {
  const inputRefs = useRef([]);

  const handleChange = (index, e) => {
    const val = e.target.value.replace(/\D/g, '');
    if (!val) return;
    const newOtp = (value || '').padEnd(length, ' ').split('');
    newOtp[index] = val.substring(val.length - 1);
    const combined = newOtp.join('').trim();
    onChange(combined);

    if (val && index < length - 1 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const newOtp = (value || '').padEnd(length, ' ').split('');
      if (newOtp[index] && newOtp[index] !== ' ') {
        newOtp[index] = ' ';
        onChange(newOtp.join('').trim());
      } else if (index > 0) {
        newOtp[index - 1] = ' ';
        onChange(newOtp.join('').trim());
        inputRefs.current[index - 1].focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1].focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (pastedData) {
      onChange(pastedData);
      const focusIndex = Math.min(pastedData.length, length - 1);
      inputRefs.current[focusIndex]?.focus();
    }
  };

  return (
    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginBottom: '15px' }}>
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="tel"
          maxLength={1}
          placeholder="-"
          value={(value || '')[index] && (value || '')[index] !== ' ' ? (value || '')[index] : ''}
          onChange={(e) => handleChange(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          className="auth-page-input"
          style={{ width: '55px', height: '55px', textAlign: 'center', fontSize: '24px', fontWeight: 'bold', padding: 0, borderRadius: '8px' }}
          autoFocus={index === 0}
        />
      ))}
    </div>
  );
};

// ================================================================
// AuthPage — renders at /login, /signup, /forgot-password
// ================================================================
const AuthPage = ({ config, authMethod, onAuthSuccess, userToken }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const method = authMethod || 'email_password';

  // Helper to safely navigate back to previous route
  const handleRedirect = useCallback(() => {
    const from = location.state?.from || '/';
    navigate(from, { replace: true });
  }, [navigate, location.state]);

  // Redirect logged-in users to home or previous route — except on
  // /forgot-password, which a logged-in user can legitimately land on from
  // the Change Password modal's "Forgot Password?" link (when they don't
  // remember their current password), and needs to actually see the
  // Email -> Code -> Reset flow instead of being bounced straight back.
  useEffect(() => {
    if (userToken && !location.pathname.startsWith('/forgot-password')) {
      handleRedirect();
    }
  }, [userToken, handleRedirect, location.pathname]);

  // Determine which view to show based on path
  const getView = useCallback(() => {
    if (location.pathname === '/signup' || location.pathname === '/register') return 'signup';
    if (location.pathname.startsWith('/forgot-password')) return 'forgotPassword';
    return 'login';
  }, [location.pathname]);

  const [view, setView] = useState(getView);

  // Sync view when URL changes — clear ALL form state for a fresh start
  useEffect(() => {
    setView(getView());
    setError('');
    setSuccessMsg('');
    setLoginPassword('');
    setShowPassword(false);
    setFpStep(1);
    setFpEmail('');
    setFpCode('');
    setFpPassword('');
    setFpPasswordConfirm('');
    setShowFpPassword(false);
  }, [location.pathname, getView]);

  // Shared state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // ---- Login (Email or Phone + Password) ----
  const [loginIdentifier, setLoginIdentifier] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // ---- Login (Phone+OTP) ----
  const [phoneStep, setPhoneStep] = useState('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);

  // ---- Signup (Details only) ----
  const [signupPhone, setSignupPhone] = useState('');
  const [signupFirstName, setSignupFirstName] = useState('');
  const [signupLastName, setSignupLastName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupAcceptTerms, setSignupAcceptTerms] = useState(false);

  // ---- Forgot Password (3-step) ----
  const [fpStep, setFpStep] = useState(1);
  const [fpEmail, setFpEmail] = useState('');
  const [fpCode, setFpCode] = useState('');
  const [fpPassword, setFpPassword] = useState('');
  const [fpPasswordConfirm, setFpPasswordConfirm] = useState('');
  const [showFpPassword, setShowFpPassword] = useState(false);
  const [fpTimer, setFpTimer] = useState(0);

  // Persistent Timer for Forgot Password Code
  useEffect(() => {
    const checkTimer = () => {
      const expiryStr = localStorage.getItem('fpCodeExpiry');
      if (expiryStr) {
        const expiry = parseInt(expiryStr, 10);
        const remaining = Math.max(0, Math.floor((expiry - Date.now()) / 1000));
        setFpTimer(remaining);
        if (remaining <= 0) {
          localStorage.removeItem('fpCodeExpiry');
        }
      } else {
        setFpTimer(0);
      }
    };

    checkTimer();
    const interval = setInterval(checkTimer, 1000);
    return () => clearInterval(interval);
  }, []);

  const formatTimer = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const handleOpenPolicy = (path) => navigate(path);

  // =========== EMAIL OR PHONE + PASSWORD LOGIN ===========
  const handleEmailLogin = async () => {
    const identifier = loginIdentifier.trim();
    if (!identifier) { setError('Please enter your email or phone number'); return; }

    const isPhone = /^[0-9+\-\s()]+$/.test(identifier);
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.(com|in|co\.in|net|org|edu|gov|io|co|biz|info)$/i;
    if (isPhone) {
      const cleanPhone = identifier.replace(/\D/g, '');
      if (cleanPhone.length !== 10) {
        setError('Please enter a valid 10-digit mobile number');
        return;
      }
    } else {
      if (!emailRegex.test(identifier)) {
        setError('Please enter a valid email address or phone number');
        return;
      }
      if (identifier.toLowerCase().includes('@gmail.') && !identifier.toLowerCase().endsWith('@gmail.com')) {
        setError('Invalid email address. Please enter a valid Gmail address.');
        return;
      }
    }

    if (!loginPassword) { setError('Please enter your password'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(API_BASE_URL + '/auth/login', {
        identifier: loginIdentifier.trim(),
        password: loginPassword
      }, { headers: getApiHeaders() });

      const data = res.data;
      if (data && data.token && data.user) {
        const { token, user } = data;
        setAuthCookies(token, user.id, user.phone || '');
        if (onAuthSuccess) onAuthSuccess(token, user.id, user.phone || '');
        handleRedirect();
      } else {
        setError('Unexpected response. Please try again.');
      }
    } catch (err) {
      const errData = err.response?.data;
      if (errData?.errors?.validation) setError(errData.errors.validation);
      else if (errData?.message) setError(errData.message);
      else setError('Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  // =========== PHONE + OTP LOGIN ===========
  const handlePhoneSendOTP = async () => {
    if (!phoneNumber || phoneNumber.length < 10) { setError('Please enter a valid 10-digit mobile number'); return; }
    if (!acceptTerms) { setError('Please accept the Terms & Conditions and Privacy Policy'); return; }
    setLoading(true);
    setError('');
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    try {
      await axios.post(API_BASE_URL + '/auth/customer-login/send-otp', {
        phone: cleanPhone, code: '+91'
      }, { headers: getApiHeaders() });
      setPhoneStep('otp');
      setPhoneNumber(cleanPhone);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP.');
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneVerifyOTP = async () => {
    const cleanOtp = otp.replace(/\D/g, '');
    if (!cleanOtp || cleanOtp.length < 4) { setError('Please enter a valid OTP'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(API_BASE_URL + '/auth/customer-login/verify-otp', {
        phone: phoneNumber.replace(/\D/g, ''),
        code: '+91',
        token: cleanOtp,
        branch_id: 1
      }, { headers: getApiHeaders() });

      if (res.data?.token && res.data?.user) {
        const { token, user } = res.data;
        setAuthCookies(token, user.id, phoneNumber);
        if (onAuthSuccess) onAuthSuccess(token, user.id, phoneNumber);
        handleRedirect();
      } else {
        setError('Invalid OTP');
      }
    } catch (err) {
      if (err.response?.status === 429) setError('Too many attempts. Please wait.');
      else setError(err.response?.data?.message || 'Invalid OTP.');
    } finally {
      setLoading(false);
    }
  };

  // =========== SIGNUP ===========


  const handleSignupRegister = async () => {
    if (!signupFirstName.trim()) { setError('First name is required'); return; }
    if (!signupLastName.trim()) { setError('Last name is required'); return; }

    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.(com|in|co\.in|net|org|edu|gov|io|co|biz|info)$/i;
    const lowerSignupEmail = signupEmail.trim().toLowerCase();
    if (!signupEmail.trim()) {
      setError('Email is required');
      return;
    } else if (!emailRegex.test(signupEmail.trim())) {
      setError('Please enter a valid email address');
      return;
    } else if (lowerSignupEmail.includes('@gmail.') && !lowerSignupEmail.endsWith('@gmail.com')) {
      setError('Invalid email address. Please enter a valid Gmail address.');
      return;
    } else if (
      lowerSignupEmail.endsWith('@gm.com') ||
      lowerSignupEmail.endsWith('@gamil.com') ||
      lowerSignupEmail.endsWith('@gmal.com')
    ) {
      setError('Invalid email address. Did you mean @gmail.com?');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await axios.post(API_BASE_URL + '/auth/signup/register', {
        first_name: signupFirstName.trim(),
        last_name: signupLastName.trim(),
        email: signupEmail.trim() || undefined,
        phone: signupPhone,
        country_code: '+91'
      }, { headers: getApiHeaders() });
      setSuccessMsg('Registration successful! A temporary password has been sent to your email. Redirecting to login...');
      setTimeout(() => {
        setSignupFirstName('');
        setSignupLastName('');
        setSignupEmail('');
        setSignupPhone('');
        setSignupAcceptTerms(false);
        navigate('/login', { replace: true });
      }, 3500);
    } catch (err) {
      const errData = err.response?.data;
      if (errData?.errors) {
        setError(Object.values(errData.errors).flat().join('. '));
      } else {
        setError(errData?.message || 'Registration failed.');
      }
    } finally {
      setLoading(false);
    }
  };

  // =========== FORGOT PASSWORD ===========
  const handleFpSendCode = async () => {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.(com|in|co\.in|net|org|edu|gov|io|co|biz|info)$/i;
    const lowerFpEmail = fpEmail.trim().toLowerCase();
    if (!fpEmail.trim()) {
      setError('Please enter your email');
      return;
    } else if (!emailRegex.test(fpEmail.trim())) {
      setError('Please enter a valid email address');
      return;
    } else if (lowerFpEmail.includes('@gmail.') && !lowerFpEmail.endsWith('@gmail.com')) {
      setError('Invalid email address. Please enter a valid Gmail address.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(API_BASE_URL + '/auth/forgot-password', {
        email: fpEmail.trim()
      }, { headers: getApiHeaders() });
      const expiryMins = parseInt(process.env.REACT_APP_RESET_CODE_EXPIRY_MINUTES, 10) || 15;

      localStorage.setItem('fpCodeExpiry', (Date.now() + expiryMins * 60 * 1000).toString());
      setFpTimer(expiryMins * 60);
      setFpStep(2);
      setSuccessMsg(res.data.message || 'Check your email for the reset code');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      const errData = err.response?.data;
      setError(errData?.errors?.email?.[0] || errData?.message || 'Failed to send reset code.');
    } finally {
      setLoading(false);
    }
  };

  const handleFpVerifyCode = async () => {
    if (fpTimer <= 0) {
      setError('Reset code has expired. Please request a new code.');
      setFpCode('');
      return;
    }
    if (!fpCode.trim() || fpCode.trim().length !== 4) { setError('Please enter a valid 4-digit reset code'); return; }
    setLoading(true);
    setError('');
    try {
      await axios.post(API_BASE_URL + '/auth/forgot-password/verify-code', {
        email: fpEmail.trim(), code: fpCode.trim()
      }, { headers: getApiHeaders() });
      localStorage.removeItem('fpCodeExpiry');
      setFpTimer(0);
      setFpStep(3);
    } catch (err) {
      const errData = err.response?.data;
      setError(errData?.errors?.code?.[0] || errData?.message || 'Invalid code.');
    } finally {
      setLoading(false);
    }
  };

  const handleFpResetPassword = async () => {
    if (!fpPassword) { setError('Please enter a new password'); return; }
    if (fpPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (fpPassword !== fpPasswordConfirm) { setError('Passwords do not match'); return; }
    setLoading(true);
    setError('');
    try {
      await axios.post(API_BASE_URL + '/auth/forgot-password/reset-password', {
        email: fpEmail.trim(),
        password: fpPassword,
        password_confirmation: fpPasswordConfirm
      }, { headers: getApiHeaders() });
      setSuccessMsg('Password reset successful! Please log in.');
      // Navigate to login directly and pass success state
      navigate('/login', { replace: true });
      // Reset forgot password states after navigation
      setFpPassword('');
      setFpPasswordConfirm('');
      setFpCode('');
      setFpEmail('');
      setFpStep(1);
      setShowFpPassword(false);
      setLoginPassword('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  // Don't render if already logged in (redirect is in progress) — except
  // on /forgot-password, which stays visible for a logged-in user (see the
  // matching exception on the redirect effect above).
  if (userToken && !location.pathname.startsWith('/forgot-password')) return null;

  const logoUrl = config?.logo;
  const restaurantName = config?.name || 'Restaurant';

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="auth-page">
      {/* Background decoration */}
      <div className="auth-page-bg">
        <div className="auth-page-bg-circle auth-page-bg-circle--1"></div>
        <div className="auth-page-bg-circle auth-page-bg-circle--2"></div>
        <div className="auth-page-bg-circle auth-page-bg-circle--3"></div>
      </div>

      <div className="auth-page-container">
        {/* Left decorative panel (desktop only) */}
        <div className="auth-page-brand-panel">
          {/* Decorative floating shapes */}
          <div className="auth-page-brand-shape shape-1"></div>
          <div className="auth-page-brand-shape shape-2"></div>
          <div className="auth-page-brand-shape shape-3"></div>

          <div className="auth-page-brand-content">
            <div className="auth-page-logo-wrapper">
              {logoUrl && <img src={logoUrl} alt={restaurantName} className="auth-page-brand-logo" />}
            </div>
            <h1 className="auth-page-brand-name">{restaurantName}</h1>
            <div className="auth-page-brand-divider"></div>
          </div>
        </div>

        {/* Right form panel */}
        <div className="auth-page-form-panel">
          <div className="auth-page-form-wrapper">
            {/* Mobile logo */}
            <div className="auth-page-mobile-header">
              {logoUrl && <img src={logoUrl} alt={restaurantName} className="auth-page-mobile-logo" />}
              <h2 className="auth-page-mobile-name">{restaurantName}</h2>
            </div>

            {/* Success banner */}
            {successMsg && <div className="auth-page-success">{successMsg}</div>}

            {/* ============ LOGIN ============ */}
            {view === 'login' && (
              <>
                {method === 'email_password' ? (
                  <>
                    <h2 className="auth-page-title">Welcome Back</h2>


                    <div className="auth-page-field">
                      <label className="auth-page-label">Email or Phone Number <span className="auth-page-required">*</span></label>
                      <div className="auth-page-input-wrap">
                        {/^\d+$/.test(loginIdentifier) ? <Phone size={18} className="auth-page-input-icon" /> : <Mail size={18} className="auth-page-input-icon" />}
                        <input type="text" placeholder="Email or phone number" value={loginIdentifier}
                          onChange={(e) => {
                            let v = e.target.value;
                            if (/^\d+$/.test(v) && v.length > 10) { v = v.substring(0, 10); }
                            setLoginIdentifier(v);
                            setError('');
                          }}
                          onKeyDown={(e) => e.key === 'Enter' && handleEmailLogin()}
                          className="auth-page-input" autoFocus />
                      </div>
                    </div>

                    <div className="auth-page-field">
                      <label className="auth-page-label">Password <span className="auth-page-required">*</span></label>
                      <div className="auth-page-input-wrap">
                        <Lock size={18} className="auth-page-input-icon" />
                        <input type={showPassword ? 'text' : 'password'} placeholder="Enter your password"
                          value={loginPassword}
                          onChange={(e) => { setLoginPassword(e.target.value); setError(''); }}
                          onKeyDown={(e) => e.key === 'Enter' && handleEmailLogin()}
                          className="auth-page-input" />
                        <button type="button" className="auth-page-pw-toggle"
                          onClick={() => setShowPassword(!showPassword)}>
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>

                    <div className="auth-page-forgot-row">
                      <Link to="/forgot-password" className="auth-page-link">Forgot Password?</Link>
                    </div>

                    {error && <div className="auth-page-error">{error}</div>}

                    <button onClick={handleEmailLogin} disabled={loading || !loginIdentifier || !loginPassword}
                      className="auth-page-btn">
                      {loading ? <><Loader size={18} className="auth-spin" /> Logging in...</> : 'Login'}
                    </button>

                    <div className="auth-page-switch">
                      <span>New user?</span>
                      <Link to="/signup" className="auth-page-link">Create Account</Link>
                    </div>
                  </>
                ) : (
                  /* ---- Phone + OTP ---- */
                  <>
                    {phoneStep === 'phone' ? (
                      <>
                        <h2 className="auth-page-title">Login</h2>
                        <p className="auth-page-subtitle">Enter your mobile number to continue</p>

                        <div className="auth-page-field">
                          <label className="auth-page-label">Mobile Number <span className="auth-page-required">*</span></label>
                          <div className="auth-page-input-wrap">
                            <Phone size={18} className="auth-page-input-icon" />
                            <input type="tel" placeholder="10-digit mobile number" value={phoneNumber}
                              onChange={(e) => {
                                const v = e.target.value.replace(/\D/g, '');
                                if (v.length <= 10) { setPhoneNumber(v); setError(''); }
                              }}
                              maxLength={10} className="auth-page-input" autoFocus />
                          </div>
                        </div>

                        <div className="auth-page-terms">
                          <input type="checkbox" id="login-terms" checked={acceptTerms}
                            onChange={(e) => { setAcceptTerms(e.target.checked); setError(''); }}
                            className="auth-page-checkbox" />
                          <label htmlFor="login-terms" className="auth-page-terms-label">
                            I accept the{' '}
                            <a href="#!" onClick={(e) => { e.preventDefault(); handleOpenPolicy('/terms-conditions'); }} className="auth-page-link">Terms & Conditions</a>
                            {' '}and{' '}
                            <a href="#!" onClick={(e) => { e.preventDefault(); handleOpenPolicy('/privacy-policy'); }} className="auth-page-link">Privacy Policy</a>
                          </label>
                        </div>

                        {error && <div className="auth-page-error">{error}</div>}

                        <button onClick={handlePhoneSendOTP}
                          disabled={loading || !phoneNumber || phoneNumber.length < 10 || !acceptTerms}
                          className="auth-page-btn">
                          {loading ? <><Loader size={18} className="auth-spin" /> Sending...</> : 'Send OTP'}
                        </button>
                      </>
                    ) : (
                      <>
                        <h2 className="auth-page-title">Verify OTP</h2>
                        <p className="auth-page-subtitle">We've sent an OTP to <strong>+91 {phoneNumber}</strong></p>

                        <div className="auth-page-field">
                          <div className="auth-page-input-wrap">
                            <input type="tel" placeholder="Enter OTP" value={otp}
                              onChange={(e) => {
                                const v = e.target.value.replace(/\D/g, '');
                                if (v.length <= 6) { setOtp(v); setError(''); }
                              }}
                              maxLength={6} className="auth-page-input auth-page-otp-input" autoFocus />
                          </div>
                        </div>

                        {error && <div className="auth-page-error">{error}</div>}

                        <button onClick={handlePhoneVerifyOTP} disabled={loading || !otp || otp.length < 4}
                          className="auth-page-btn">
                          {loading ? <><Loader size={18} className="auth-spin" /> Verifying...</> : 'Verify OTP'}
                        </button>

                        <div className="auth-page-resend">
                          <span>Didn't receive OTP?</span>
                          <button onClick={() => { setOtp(''); setError(''); handlePhoneSendOTP(); }}
                            className="auth-page-link-btn" disabled={loading}>Resend OTP</button>
                        </div>

                        <button onClick={() => { setPhoneStep('phone'); setOtp(''); setError(''); }}
                          className="auth-page-back-btn">
                          <ArrowLeft size={16} /> Change mobile number
                        </button>
                      </>
                    )}
                  </>
                )}
              </>
            )}

            {/* ============ SIGNUP ============ */}
            {view === 'signup' && (
              <>
                {/* Step indicator removed for 1-step signup */}

                <>
                  <h2 className="auth-page-title">Create Account</h2>
                  <p className="auth-page-subtitle">Fill in your information to join us</p>

                  <div className="auth-page-field-row">
                    <div className="auth-page-field">
                      <label className="auth-page-label">First Name <span className="auth-page-required">*</span></label>
                      <div className="auth-page-input-wrap">
                        <User size={18} className="auth-page-input-icon" />
                        <input type="text" placeholder="First Name" value={signupFirstName}
                          onChange={(e) => { setSignupFirstName(e.target.value.replace(/[^a-zA-Z\s]/g, '')); setError(''); }}
                          className="auth-page-input" autoFocus />
                      </div>
                    </div>

                    <div className="auth-page-field">
                      <label className="auth-page-label">Last Name <span className="auth-page-required">*</span></label>
                      <div className="auth-page-input-wrap">
                        <User size={18} className="auth-page-input-icon" />
                        <input type="text" placeholder="Last Name" value={signupLastName}
                          onChange={(e) => { setSignupLastName(e.target.value.replace(/[^a-zA-Z\s]/g, '')); setError(''); }}
                          className="auth-page-input" />
                      </div>
                    </div>
                  </div>

                  <div className="auth-page-field">
                    <label className="auth-page-label">Mobile Number <span className="auth-page-required">*</span></label>
                    <div className="auth-page-input-wrap">
                      <Phone size={18} className="auth-page-input-icon" />
                      <input type="tel" placeholder="10-digit mobile number" value={signupPhone}
                        onChange={(e) => {
                          const v = e.target.value.replace(/\D/g, '');
                          if (v.length <= 10) { setSignupPhone(v); setError(''); }
                        }}
                        maxLength={10} className="auth-page-input" />
                    </div>
                  </div>

                  <div className="auth-page-field">
                    <label className="auth-page-label">Email <span className="auth-page-required">*</span></label>
                    <div className="auth-page-input-wrap">
                      <Mail size={18} className="auth-page-input-icon" />
                      <input type="email" placeholder="you@example.com" value={signupEmail}
                        onChange={(e) => { setSignupEmail(e.target.value); setError(''); }}
                        className="auth-page-input" />
                    </div>
                  </div>

                  <div className="auth-page-terms">
                    <input type="checkbox" id="signup-terms" checked={signupAcceptTerms}
                      onChange={(e) => { setSignupAcceptTerms(e.target.checked); setError(''); }}
                      className="auth-page-checkbox" />
                    <label htmlFor="signup-terms" className="auth-page-terms-label">
                      I accept the{' '}
                      <a href="#!" onClick={(e) => { e.preventDefault(); handleOpenPolicy('/terms-conditions'); }} className="auth-page-link">Terms & Conditions</a>
                      {' '}and{' '}
                      <a href="#!" onClick={(e) => { e.preventDefault(); handleOpenPolicy('/privacy-policy'); }} className="auth-page-link">Privacy Policy</a>
                    </label>
                  </div>

                  {error && <div className="auth-page-error">{error}</div>}

                  <button onClick={handleSignupRegister}
                    disabled={loading || !signupFirstName.trim() || !signupLastName.trim() || !signupPhone || signupPhone.length < 10 || !signupEmail.trim() || !signupAcceptTerms}
                    className="auth-page-btn">
                    {loading ? <><Loader size={18} className="auth-spin" /> Creating Account...</> : 'Create Account'}
                  </button>

                  <div className="auth-page-switch">
                    <span>Already have an account?</span>
                    <Link to="/login" className="auth-page-link">Login</Link>
                  </div>
                </>
              </>
            )}

            {/* ============ FORGOT PASSWORD ============ */}
            {view === 'forgotPassword' && (
              <>
                <div className="auth-page-steps">
                  <div className={`auth-page-step ${fpStep >= 1 ? 'active' : ''}`}><span>1</span><small>Email</small></div>
                  <div className={`auth-page-step-line ${fpStep >= 2 ? 'active' : ''}`}></div>
                  <div className={`auth-page-step ${fpStep >= 2 ? 'active' : ''}`}><span>2</span><small>Code</small></div>
                  <div className={`auth-page-step-line ${fpStep >= 3 ? 'active' : ''}`}></div>
                  <div className={`auth-page-step ${fpStep >= 3 ? 'active' : ''}`}><span>3</span><small>Reset</small></div>
                </div>

                {fpStep === 1 && (
                  <>
                    <h2 className="auth-page-title">Forgot Password</h2>
                    <p className="auth-page-subtitle">Enter your email to receive a reset code</p>

                    <div className="auth-page-field">
                      <label className="auth-page-label">Email Address <span className="auth-page-required">*</span></label>
                      <div className="auth-page-input-wrap">
                        <Mail size={18} className="auth-page-input-icon" />
                        <input type="email" placeholder="you@example.com" value={fpEmail}
                          onChange={(e) => { setFpEmail(e.target.value); setError(''); }}
                          onKeyDown={(e) => e.key === 'Enter' && handleFpSendCode()}
                          className="auth-page-input" autoFocus />
                      </div>
                    </div>

                    {error && <div className="auth-page-error">{error}</div>}

                    <button onClick={handleFpSendCode} disabled={loading || !fpEmail.trim()}
                      className="auth-page-btn">
                      {loading ? <><Loader size={18} className="auth-spin" /> Sending...</> : 'Send Reset Code'}
                    </button>

                    <Link to="/login" className="auth-page-back-btn">
                      <User size={16} /> Back to Login
                    </Link>
                  </>
                )}

                {fpStep === 2 && (
                  <>
                    <h2 className="auth-page-title">Verify Code</h2>
                    <p className="auth-page-subtitle">Enter the code sent to <strong>{fpEmail}</strong></p>

                    {fpTimer > 0 && (
                      <div style={{ textAlign: 'center', marginBottom: '12px', fontSize: '14px', color: fpTimer <= 60 ? '#e53e3e' : '#666' }}>
                        Code expires in <strong>{formatTimer(fpTimer)}</strong>
                      </div>
                    )}
                    {fpTimer <= 0 && (
                      <div style={{ textAlign: 'center', marginBottom: '12px', fontSize: '14px', color: '#e53e3e', fontWeight: '600' }}>
                        Code has expired. Please resend.
                      </div>
                    )}

                    <div className="auth-page-field">
                      <OtpBoxInput length={4} value={fpCode} onChange={(val) => { setFpCode(val); setError(''); }} />
                    </div>

                    {error && <div className="auth-page-error">{error}</div>}

                    <button onClick={handleFpVerifyCode} disabled={loading || !fpCode.trim() || fpTimer <= 0}
                      className="auth-page-btn">
                      {loading ? <><Loader size={18} className="auth-spin" /> Verifying...</> : 'Verify Code'}
                    </button>

                    {fpTimer <= 0 && (
                      <button onClick={() => { setFpCode(''); setError(''); handleFpSendCode(); }}
                        disabled={loading}
                        className="auth-page-btn" style={{ marginTop: '8px', background: '#fff', color: '#e65100', border: '2px solid #e65100', fontWeight: '600' }}>
                        Resend Code
                      </button>
                    )}

                    <button onClick={() => {
                      setFpStep(1);
                      setFpCode('');
                      setError('');
                      localStorage.removeItem('fpCodeExpiry');
                      setFpTimer(0);
                    }}
                      className="auth-page-back-btn">
                      <ArrowLeft size={16} /> Change email
                    </button>
                  </>
                )}

                {fpStep === 3 && (
                  <>
                    <h2 className="auth-page-title">Set New Password</h2>
                    <p className="auth-page-subtitle">Create a strong new password</p>

                    <div className="auth-page-field">
                      <label className="auth-page-label">New Password <span className="auth-page-required">*</span></label>
                      <div className="auth-page-input-wrap">
                        <Lock size={18} className="auth-page-input-icon" />
                        <input type={showFpPassword ? 'text' : 'password'} placeholder="Min 6 characters"
                          value={fpPassword}
                          onChange={(e) => { setFpPassword(e.target.value); setError(''); }}
                          className="auth-page-input" autoFocus />
                        <button type="button" className="auth-page-pw-toggle"
                          onClick={() => setShowFpPassword(!showFpPassword)}>
                          {showFpPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>

                    <div className="auth-page-field">
                      <label className="auth-page-label">Confirm New Password <span className="auth-page-required">*</span></label>
                      <div className="auth-page-input-wrap">
                        <Lock size={18} className="auth-page-input-icon" />
                        <input type={showFpPassword ? 'text' : 'password'} placeholder="Re-enter password"
                          value={fpPasswordConfirm}
                          onChange={(e) => { setFpPasswordConfirm(e.target.value); setError(''); }}
                          className="auth-page-input" />
                      </div>
                    </div>

                    {error && <div className="auth-page-error">{error}</div>}

                    <button onClick={handleFpResetPassword}
                      disabled={loading || !fpPassword || !fpPasswordConfirm}
                      className="auth-page-btn">
                      {loading ? <><Loader size={18} className="auth-spin" /> Resetting...</> : 'Reset Password'}
                    </button>
                  </>
                )}
              </>
            )}

            {/* Footer */}
            <div className="auth-page-footer">
              <Link to="/" className="auth-page-home-link">
                <Home size={16} /> Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
